/**
 * Helpers for turning a flat span list into a renderable trace timeline.
 */

export const RUN_TYPE_STYLES = {
    llm: { label: 'LLM', dot: 'bg-violet-500', bar: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 ring-violet-600/20' },
    retriever: { label: 'Retriever', dot: 'bg-sky-500', bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 ring-sky-600/20' },
    tool: { label: 'Tool', dot: 'bg-amber-500', bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
    chain: { label: 'Chain', dot: 'bg-emerald-500', bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' },
};

export const DEFAULT_RUN_TYPE_STYLE = {
    label: 'Span', dot: 'bg-gray-400', bar: 'bg-gray-400', chip: 'bg-gray-50 text-gray-700 ring-gray-600/20',
};

export function runTypeStyle(runType) {
    return RUN_TYPE_STYLES[(runType || '').toLowerCase()] || DEFAULT_RUN_TYPE_STYLE;
}

const toMillis = value => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Build a parent/child tree from a flat span list.
 *
 * Spans whose parent is missing from the page (the parent may have been
 * truncated away, or arrived in a later batch) are promoted to roots so no
 * span is ever silently dropped.
 */
export function buildTraceTree(spans = []) {
    const nodes = new Map();

    spans.forEach(span => {
        nodes.set(span.spanId, { ...span, children: [] });
    });

    const roots = [];
    nodes.forEach(node => {
        const parent = node.parentSpanId ? nodes.get(node.parentSpanId) : null;
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });

    const byStart = (a, b) => (toMillis(a.startedAt) || 0) - (toMillis(b.startedAt) || 0);
    roots.sort(byStart);
    nodes.forEach(node => node.children.sort(byStart));

    return roots;
}

/**
 * Overall time window covered by the spans, used to scale the waterfall bars.
 */
export function traceWindow(spans = []) {
    let start = null;
    let end = null;

    spans.forEach(span => {
        const spanStart = toMillis(span.startedAt);
        if (spanStart !== null && (start === null || spanStart < start)) {
            start = spanStart;
        }

        const spanEnd = toMillis(span.endedAt)
            || (spanStart !== null && span.durationMs ? spanStart + span.durationMs : null);
        if (spanEnd !== null && (end === null || spanEnd > end)) {
            end = spanEnd;
        }
    });

    if (start === null) {
        return { start: 0, end: 0, duration: 0 };
    }

    // Guard against a zero-width window so bars stay visible.
    const duration = Math.max((end ?? start) - start, 1);
    return { start, end: end ?? start, duration };
}

/**
 * Position of one span's bar within the trace window, as percentages.
 */
export function barGeometry(span, window) {
    const spanStart = toMillis(span.startedAt);
    if (spanStart === null || !window.duration) {
        return { offset: 0, width: 0 };
    }

    const spanEnd = toMillis(span.endedAt)
        || (span.durationMs ? spanStart + span.durationMs : spanStart);

    const offset = ((spanStart - window.start) / window.duration) * 100;
    const width = ((spanEnd - spanStart) / window.duration) * 100;

    return {
        offset: Math.max(0, Math.min(100, offset)),
        // Keep a sliver visible for very fast spans.
        width: Math.max(0.5, Math.min(100 - offset, width)),
    };
}

/**
 * Flatten the tree into render order, carrying depth and collapsed state.
 */
export function flattenTree(roots, collapsed) {
    const rows = [];

    const walk = (node, depth) => {
        rows.push({ node, depth });
        if (collapsed.has(node.spanId)) {
            return;
        }
        node.children.forEach(child => walk(child, depth + 1));
    };

    roots.forEach(root => walk(root, 0));
    return rows;
}

export function formatDuration(ms) {
    if (ms === null || ms === undefined) return '-';
    if (ms < 1) return '<1ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
}

export function formatTime(value) {
    const ms = toMillis(value);
    if (ms === null) return '-';
    return new Date(ms).toLocaleTimeString(undefined, {
        hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

export function formatDateTime(value) {
    const ms = toMillis(value);
    if (ms === null) return '-';
    return new Date(ms).toLocaleString();
}

/**
 * Relative age, e.g. "3m ago" - used in the session list.
 */
export function formatRelative(value) {
    const ms = toMillis(value);
    if (ms === null) return '';

    const seconds = Math.floor((Date.now() - ms) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(ms).toLocaleDateString();
}

/**
 * Pretty-print a span payload for the detail panel.
 */
export function formatPayload(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch (err) {
        return String(value);
    }
}

/**
 * Roll up a trace for the summary bar: span count, error count, total time.
 */
export function traceSummary(spans = []) {
    const window = traceWindow(spans);
    const errors = spans.filter(span => span.status === 'error').length;
    const cancelled = spans.filter(span => span.status === 'cancelled').length;
    const llmCalls = spans.filter(span => (span.runType || '').toLowerCase() === 'llm').length;

    return {
        total: spans.length,
        errors,
        cancelled,
        llmCalls,
        duration: window.duration && spans.length ? window.duration : 0,
    };
}
